import { useQueryClient } from '@tanstack/react-query';
import { MoreHorizontal, Pencil, Plus, Power, PowerOff, Tags, Ticket } from 'lucide-react';
import { useState } from 'react';

import { useCupomCategoriasControllerListar } from '@/api/generated/admin-cupom-categorias/admin-cupom-categorias';
import {
  getCuponsControllerListarQueryKey,
  useCuponsControllerDefinirAtivo,
  useCuponsControllerListar,
} from '@/api/generated/admin-cupons/admin-cupons';
import type { CupomResponse } from '@/api/generated/model';
import { ApiError } from '@/api/http-client';
import { CategoriasDialogo } from '@/features/admin/cupons/components/CategoriasDialogo';
import { CupomFormDialogo } from '@/features/admin/cupons/components/CupomFormDialogo';
import { EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { usePermissoes } from '@/shared/permissoes/usePermissoes';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { Input } from '@/shared/ui/input';
import { Paginacao } from '@/shared/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { Skeleton } from '@/shared/ui/skeleton';
import { toast } from '@/shared/ui/sonner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';

const TODOS = 'todos';

function formatarData(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '—';
}

/** Código da faixa única, ou "N faixas" quando o cupom tem mais de uma. */
function descreverCodigo(cupom: CupomResponse): string {
  const faixa = cupom.faixas[0];
  if (cupom.faixas.length <= 1) return faixa?.codigo ?? faixa?.linkRedirecionamento ?? '—';
  return `${cupom.faixas.length} faixas`;
}

/** "Todos" · "≥ 2" · "≥ 2 a ≥ 5" quando o cupom tem faixas em ranques diferentes. */
function descreverRanques(cupom: CupomResponse): string {
  if (cupom.faixas.length === 0) return '—';
  const ranques = cupom.faixas.map((f) => f.ranqueMinimo);
  const minimo = Math.min(...ranques);
  if (cupom.faixas.length === 1) return minimo === 0 ? 'Todos' : `≥ ${minimo}`;
  return `≥ ${minimo} a ≥ ${Math.max(...ranques)}`;
}

/** "01/02/2026 – 31/03/2026" · "até 31/03/2026" · "sem prazo". */
function descreverVigencia(cupom: CupomResponse): string {
  if (!cupom.validoDe && !cupom.validoAte) return 'Sem prazo';
  if (cupom.validoDe && cupom.validoAte) {
    return `${formatarData(cupom.validoDe)} – ${formatarData(cupom.validoAte)}`;
  }
  return cupom.validoDe
    ? `a partir de ${formatarData(cupom.validoDe)}`
    : `até ${formatarData(cupom.validoAte)}`;
}

/**
 * Cupons do Clube (F6-5).
 *
 * ⚠️ A listagem NÃO carrega `regras` — o campo só existe no detalhe. Não é
 * economia de payload: é a condição comercial do parceiro, que não pode
 * escorrer para a vitrine por herança de objeto.
 */
export function ClubeCuponsTab() {
  const { pode } = usePermissoes();
  const queryClient = useQueryClient();

  const [pagina, setPagina] = useState(1);
  const [busca, setBusca] = useState('');
  const [categoriaId, setCategoriaId] = useState<string>(TODOS);
  const [situacao, setSituacao] = useState<string>(TODOS);
  const [formAberto, setFormAberto] = useState(false);
  const [emEdicao, setEmEdicao] = useState<CupomResponse | undefined>();
  const [categoriasAbertas, setCategoriasAbertas] = useState(false);

  const { data, isPending, isError, refetch } = useCuponsControllerListar({
    pagina,
    porPagina: 20,
    ordenarPor: 'titulo',
    ordem: 'asc',
    ...(busca.trim() ? { busca: busca.trim() } : {}),
    ...(categoriaId !== TODOS ? { categoriaId } : {}),
    ...(situacao !== TODOS ? { ativo: situacao as 'true' | 'false' } : {}),
  });

  const { data: categorias } = useCupomCategoriasControllerListar();
  const definirAtivo = useCuponsControllerDefinirAtivo();

  const podeCriar = pode('admin_cupons.criar');
  const podeEditar = pode('admin_cupons.editar');

  function aoFiltrar(aplicar: () => void) {
    aplicar();
    setPagina(1);
  }

  async function alternarAtivo(cupom: CupomResponse, ativo: boolean) {
    try {
      await definirAtivo.mutateAsync({ id: cupom.id, data: { ativo } });
      await queryClient.invalidateQueries({ queryKey: getCuponsControllerListarQueryKey() });
      toast.success(ativo ? `"${cupom.titulo}" reativado.` : `"${cupom.titulo}" desativado.`);
    } catch (erro) {
      // O 409 de código duplicado ao reativar chega por aqui, com a mensagem
      // do servidor dizendo qual cupom ativo já usa o código.
      toast.error(erro instanceof ApiError ? erro.message : 'Não foi possível alterar o cupom.');
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
        <div className="flex flex-col sm:flex-row gap-3 flex-1">
          <Input
            value={busca}
            onChange={(e) => aoFiltrar(() => setBusca(e.target.value))}
            placeholder="Buscar por título ou código..."
            className="sm:max-w-xs"
          />

          {categorias && categorias.itens.length > 0 && (
            <Select value={categoriaId} onValueChange={(v) => aoFiltrar(() => setCategoriaId(v))}>
              <SelectTrigger className="sm:w-48" aria-label="Filtrar por categoria">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={TODOS}>Todas as categorias</SelectItem>
                {categorias.itens.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <Select value={situacao} onValueChange={(v) => aoFiltrar(() => setSituacao(v))}>
            <SelectTrigger className="sm:w-40" aria-label="Filtrar por status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={TODOS}>Todos os status</SelectItem>
              <SelectItem value="true">Ativos</SelectItem>
              <SelectItem value="false">Desativados</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setCategoriasAbertas(true)}>
            <Tags className="w-4 h-4" />
            Categorias
          </Button>

          {podeCriar && (
            <Button
              onClick={() => {
                setEmEdicao(undefined);
                setFormAberto(true);
              }}
            >
              <Plus className="w-4 h-4" />
              Novo cupom
            </Button>
          )}
        </div>
      </div>

      <div className="bg-surface-raised rounded-lg border-2 border-brand/20 overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-brand/5 to-transparent border-b border-brand/10">
          <h3 className="text-sm font-semibold text-brand uppercase tracking-wide">
            Cupons do Clube
          </h3>
        </div>

        {isPending ? (
          <div className="p-6 space-y-3">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-10 rounded-md" />
            ))}
          </div>
        ) : isError || !data ? (
          <EstadoErro aoTentarDeNovo={() => void refetch()} />
        ) : data.itens.length === 0 ? (
          <EstadoVazio
            icone={Ticket}
            titulo="Nenhum cupom encontrado"
            descricao={busca ? 'Tente outro termo de busca.' : 'Crie o primeiro cupom.'}
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cupom</TableHead>
                    <TableHead>Categoria</TableHead>
                    <TableHead>Ranque</TableHead>
                    <TableHead>Vigência</TableHead>
                    <TableHead>Resgates</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-20">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.itens.map((cupom) => (
                    <TableRow key={cupom.id}>
                      <TableCell>
                        <span className="font-medium text-foreground">{cupom.titulo}</span>
                        <p className="text-xs text-muted-foreground mt-0.5">{descreverCodigo(cupom)}</p>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {cupom.categoria?.nome ?? '—'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {descreverRanques(cupom)}
                      </TableCell>
                      <TableCell className="text-muted-foreground whitespace-nowrap">
                        {descreverVigencia(cupom)}
                      </TableCell>
                      <TableCell className="text-muted-foreground">{cupom.totalResgates}</TableCell>
                      <TableCell>
                        {cupom.ativo ? (
                          <Badge
                            variant="outline"
                            className="text-emerald-600 border-emerald-600/40"
                          >
                            Ativo
                          </Badge>
                        ) : (
                          <Badge
                            variant="outline"
                            className="text-destructive border-destructive/40"
                          >
                            Desativado
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`Ações de ${cupom.titulo}`}
                            >
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {podeEditar && (
                              <>
                                <DropdownMenuItem
                                  onClick={() => {
                                    setEmEdicao(cupom);
                                    setFormAberto(true);
                                  }}
                                >
                                  <Pencil className="w-4 h-4" />
                                  Editar
                                </DropdownMenuItem>

                                {cupom.ativo ? (
                                  <DropdownMenuItem
                                    onClick={() => void alternarAtivo(cupom, false)}
                                    className="text-destructive focus:text-destructive"
                                  >
                                    <PowerOff className="w-4 h-4" />
                                    Desativar
                                  </DropdownMenuItem>
                                ) : (
                                  <DropdownMenuItem onClick={() => void alternarAtivo(cupom, true)}>
                                    <Power className="w-4 h-4" />
                                    Reativar
                                  </DropdownMenuItem>
                                )}
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <Paginacao
              pagina={data.pagina}
              totalPaginas={data.totalPaginas}
              total={data.total}
              porPagina={data.porPagina}
              aoMudarPagina={setPagina}
            />
          </>
        )}
      </div>

      <CupomFormDialogo
        aberto={formAberto}
        aoFechar={() => setFormAberto(false)}
        {...(emEdicao ? { cupom: emEdicao } : {})}
      />

      <CategoriasDialogo
        aberto={categoriasAbertas}
        aoFechar={() => setCategoriasAbertas(false)}
      />
    </div>
  );
}
